<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BarChart from 'shared/components/charts/BarChart.vue';
import AnalyticsPanel from '../analytics/AnalyticsPanel.vue';
import MetricValue from '../analytics/MetricValue.vue';
import { dayLabel } from '../analytics/analyticsHelpers';
import { useAnalyticsChart } from '../analytics/useAnalyticsChart';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import { teamLabel } from './atendimentoLabels';
import { QUEM_CORES } from './quemCores';

// "Canais e times": o que eram quatro abas (caixa, time, atendente e agente) vira
// um "comparar por" sobre a mesma tabela, e as mensagens da IA e de pessoas por
// dia. Sem disparo, sem nota e sem teste.
const props = defineProps({
  // A resposta de GET atendimento/comparar; null enquanto não chegou.
  report: { type: Object, default: null },
  failed: { type: Boolean, default: false },
});

const by = defineModel('by', { type: String, default: 'inbox' });

const { t } = useI18n();
const { number, percent, duration } = useAnalyticsFormat();
const { palette, bar, chartOptions } = useAnalyticsChart();

const DIMENSIONS = ['inbox', 'team', 'user', 'agent'];
const dimensionLabel = key =>
  t(`ATENDIMENTO_REPORTS.CHANNELS.BY.${key.toUpperCase()}`);

const rows = computed(() => props.report?.rows || []);

// A linha sem time (ou sem atendente) é a das conversas que não têm um; a sem
// nome é a do que a conta apagou.
const rowName = row => {
  if (row.key === null)
    return t(
      `ATENDIMENTO_REPORTS.CHANNELS.NONE.${props.report.dimension.toUpperCase()}`
    );
  if (!row.name)
    return t(
      `ATENDIMENTO_REPORTS.CHANNELS.REMOVED.${props.report.dimension.toUpperCase()}`
    );
  return props.report.dimension === 'team' ? teamLabel(row.name) : row.name;
};

const rowDetail = row => {
  if (row.detail?.length) return row.detail.join(', ');
  return props.report?.dimension === 'inbox'
    ? t('ATENDIMENTO_REPORTS.CHANNELS.NO_AGENT')
    : '';
};

// "3.140 · 1.020": as mensagens da IA e as de pessoas.
const messagesValue = row =>
  t('ATENDIMENTO_REPORTS.CHANNELS.MESSAGES_VALUE', {
    ai: number(row.ai_messages),
    people: number(row.human_messages),
  });

const daily = computed(() => props.report?.daily || []);
const hasMessages = computed(() =>
  daily.value.some(day => day.ai_messages > 0 || day.human_messages > 0)
);
const collection = computed(() => ({
  labels: daily.value.map(day => dayLabel(day.day)),
  datasets: [
    bar(
      t('ATENDIMENTO_REPORTS.CHANNELS.AI'),
      daily.value.map(day => day.ai_messages),
      palette.value.ia
    ),
    bar(
      t('ATENDIMENTO_REPORTS.CHANNELS.PEOPLE'),
      daily.value.map(day => day.human_messages),
      palette.value.pessoa
    ),
  ],
}));
const options = computed(() => chartOptions(number));
</script>

<template>
  <AnalyticsPanel
    data-test-id="atendimento-canais"
    :title="t('ATENDIMENTO_REPORTS.CHANNELS.TITLE')"
  >
    <template #aside>
      <div
        role="group"
        :aria-label="t('ATENDIMENTO_REPORTS.CHANNELS.COMPARE_BY')"
        class="flex flex-wrap p-0.5 max-w-full rounded-lg border border-n-weak bg-n-alpha-1"
      >
        <button
          v-for="key in DIMENSIONS"
          :key="key"
          type="button"
          :data-test-id="`atendimento-canais-por-${key}`"
          :aria-pressed="by === key"
          class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
          :class="
            by === key
              ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="by = key"
        >
          {{ dimensionLabel(key) }}
        </button>
      </div>
    </template>

    <p v-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.FAILED') }}
    </p>

    <template v-else-if="report">
      <p v-if="!rows.length" class="mb-0 text-sm text-n-slate-11">
        {{ t('ATENDIMENTO_REPORTS.CHANNELS.EMPTY') }}
      </p>
      <div v-else data-test-id="atendimento-canais-tabela" class="min-w-0">
        <table class="hidden w-full text-sm md:table">
          <thead>
            <tr class="text-xs text-n-slate-11">
              <th class="py-2 font-medium text-start">
                {{ dimensionLabel(report.dimension) }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.CHANNELS.CONVERSATIONS') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.CHANNELS.AI_ATTENDED') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.CHANNELS.HANDED_OFF') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.CHANNELS.FIRST_REPLY') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.CHANNELS.RESOLVED') }}
              </th>
              <th class="py-2 font-medium text-end">
                {{ t('ATENDIMENTO_REPORTS.CHANNELS.MESSAGES') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="row in rows" :key="String(row.key)">
              <td class="py-2">
                <span class="block font-medium text-n-slate-12">
                  {{ rowName(row) }}
                </span>
                <span
                  v-if="rowDetail(row)"
                  class="block text-xs text-n-slate-11"
                >
                  {{ rowDetail(row) }}
                </span>
              </td>
              <td class="py-2 tabular-nums text-end text-n-slate-12">
                {{ number(row.conversations) }}
              </td>
              <td class="py-2 tabular-nums text-end text-n-slate-12">
                {{ number(row.ai_attended.count) }}
                <span
                  v-if="row.ai_attended.percent !== null"
                  class="block text-xs text-n-slate-11"
                >
                  {{ percent(row.ai_attended.percent) }}
                </span>
              </td>
              <td class="py-2 tabular-nums text-end text-n-slate-12">
                {{ number(row.handed_off.count) }}
                <span
                  v-if="row.handed_off.percent !== null"
                  class="block text-xs text-n-slate-11"
                >
                  {{ percent(row.handed_off.percent) }}
                </span>
              </td>
              <td class="py-2 tabular-nums text-end text-n-slate-12">
                <MetricValue
                  :value="row.first_reply_seconds.p50"
                  :format="duration"
                />
                <span
                  v-if="typeof row.first_reply_seconds.p90 === 'number'"
                  class="block text-xs text-n-slate-11"
                >
                  {{
                    t('ATENDIMENTO_REPORTS.AI.PER_AGENT.P90', {
                      value: duration(row.first_reply_seconds.p90),
                    })
                  }}
                </span>
              </td>
              <td class="py-2 tabular-nums text-end text-n-slate-12">
                {{ number(row.resolved) }}
              </td>
              <td class="py-2 tabular-nums text-end text-n-slate-12">
                {{ messagesValue(row) }}
              </td>
            </tr>
          </tbody>
        </table>

        <ul class="flex flex-col gap-2 list-none md:hidden">
          <li
            v-for="row in rows"
            :key="String(row.key)"
            class="flex flex-col gap-1 p-3 text-sm rounded-lg border border-n-weak"
          >
            <span class="font-medium text-n-slate-12">
              {{ rowName(row) }}
            </span>
            <span v-if="rowDetail(row)" class="text-xs text-n-slate-11">
              {{ rowDetail(row) }}
            </span>
            <span class="flex gap-3 justify-between text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.CHANNELS.CONVERSATIONS') }}
              <span class="tabular-nums text-n-slate-12">
                {{ number(row.conversations) }}
              </span>
            </span>
            <span class="flex gap-3 justify-between text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.CHANNELS.AI_ATTENDED') }}
              <span class="tabular-nums text-n-slate-12">
                {{ number(row.ai_attended.count) }}
              </span>
            </span>
            <span class="flex gap-3 justify-between text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.CHANNELS.HANDED_OFF') }}
              <span class="tabular-nums text-n-slate-12">
                {{ number(row.handed_off.count) }}
              </span>
            </span>
            <span class="flex gap-3 justify-between text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.CHANNELS.FIRST_REPLY') }}
              <MetricValue
                class="tabular-nums text-n-slate-12"
                :value="row.first_reply_seconds.p50"
                :format="duration"
              />
            </span>
            <span class="flex gap-3 justify-between text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.CHANNELS.RESOLVED') }}
              <span class="tabular-nums text-n-slate-12">
                {{ number(row.resolved) }}
              </span>
            </span>
            <span class="flex gap-3 justify-between text-n-slate-11">
              {{ t('ATENDIMENTO_REPORTS.CHANNELS.MESSAGES') }}
              <span class="tabular-nums text-n-slate-12">
                {{ messagesValue(row) }}
              </span>
            </span>
          </li>
        </ul>
      </div>

      <section class="flex flex-col gap-2 min-w-0">
        <header
          class="flex flex-wrap gap-x-4 gap-y-1 justify-between items-center"
        >
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('ATENDIMENTO_REPORTS.CHANNELS.DAILY_TITLE') }}
          </h3>
          <div class="flex gap-3 items-center text-xs text-n-slate-11">
            <span class="flex gap-1.5 items-center">
              <span class="rounded-sm size-2.5" :class="QUEM_CORES.ia.bg" />
              {{ t('ATENDIMENTO_REPORTS.CHANNELS.AI') }}
            </span>
            <span class="flex gap-1.5 items-center">
              <span class="rounded-sm size-2.5" :class="QUEM_CORES.pessoa.bg" />
              {{ t('ATENDIMENTO_REPORTS.CHANNELS.PEOPLE') }}
            </span>
          </div>
        </header>
        <div class="relative h-56">
          <BarChart
            v-if="hasMessages"
            :collection="collection"
            :chart-options="options"
          />
          <p
            v-else
            class="flex justify-center items-center mb-0 h-full text-sm text-n-slate-11"
          >
            {{ t('ATENDIMENTO_REPORTS.CHANNELS.DAILY_EMPTY') }}
          </p>
        </div>
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('ATENDIMENTO_REPORTS.CHANNELS.DAILY_HINT') }}
        </p>
      </section>
    </template>
  </AnalyticsPanel>
</template>
